import { Box, Typography } from "@mui/material";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDimensions, usePayment } from "../hooks";
import { PaymentMethod } from "../models/payment";
import { CorporateService } from "../models/Service";
import BoxBillPayment from "../shared/boxBillPayment";
import CustomInput from "./inputs/cutsomInput";
import PaymentMethods from "./paymentMethods";
import PhoneNumberInputs from "./phoneNumberInput";
import { useQueryPaymentMethods } from "../api/queries";
import { digitsOnly, getStorageToken } from "../utils";
import ServiceChargeSummary from "./ServiceChargeSummary";

/**
 * @deprecated Superseded by DepositFundTemplate's search journey. Kept compiling
 * rather than deleted so the way back from the rewrite is not a revert on a live
 * payment path; removal is TODO-245, homed in Phase 15.
 */
const PaymentForSearch = ({
  service,
  payments,
}: {
  service: CorporateService;
  payments: PaymentMethod[];
}) => {
  const token = getStorageToken();
  const { isSmall } = useDimensions();
  const { currentStep, amount } = usePayment();
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const { setAmount } = usePayment();

  const paymentMethods = useQueryPaymentMethods({
    corporateId: String(service?.corporate_subsidiary_id!),
    enabled: !!token,
    authorisation: token || "",
  });

  const amountValue = watch("customerAmount");

  // MPAY-32393: this ran parseFloat on an unfiltered field, so "100.99" was
  // accepted and "0100.50" was renormalised before anyone saw it -- while
  // validation read parseInt and the body sent Number(). The field is now
  // filtered to whole units at the keystroke and this only mirrors it.
  useEffect(() => {
    if (amountValue !== undefined) {
      setAmount(String(amountValue));
    }
  }, [amountValue, setAmount]);

  return (
    <Box>
      <Box
        display={isSmall || currentStep === 3 ? "block" : "flex"}
        justifyContent={"space-between"}
        alignItems="flex-start"
        paddingX={isSmall ? 2 : 10}
      >
        <BoxBillPayment service={service} />
        {currentStep === 3 && (
          <ServiceChargeSummary currency={service?.currency} />
        )}

        {currentStep === 2 && (
          <Box
            sx={{
              width: isSmall ? "unset" : "40%",
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>
              {t("payment.paymentForSearch.step1_title")}
            </Typography>
            <Box>
              <CustomInput
                id={"account_number-id"}
                control={control}
                name="customerAmount"
                sx={[{ width: "100%", borderColor: "red", color: "red" }]}
                variant="outlined"
                label={t("payment.paymentForSearch.amount_label")}
                defaultValue={amount}
                transform={digitsOnly}
                rules={{
                  required: t("errors.field_required"),
                }}
                placeholder={t("payment.paymentForSearch.amount_placeholder")}
              />
              <Typography sx={{ fontSize: 11 }}>
                {t("payment.paymentForSearch.amount_hint")}
              </Typography>
            </Box>

            <PaymentMethods
              payments={paymentMethods?.data as PaymentMethod[]}
            />
            <PhoneNumberInputs service={service} isVertical />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PaymentForSearch;
